import axios from 'axios';
import { getApiBaseUrl } from './apiBaseUrl';

// Create axios instance with base URL
const api = axios.create({
  headers: {
    'Content-Type': 'application/json'
  }
});

// Request interceptor to add JWT token
api.interceptors.request.use(
  (config) => {
    config.baseURL = getApiBaseUrl();

    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Let the browser set multipart boundary — manual Content-Type breaks uploads
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response.data,
  (error) => {
    // Log the error for debugging
    console.error('[API Error]', {
      url: error.config?.url,
      method: error.config?.method,
      status: error.response?.status,
      data: error.response?.data,
      message: error.message
    });
    
    // Don't auto-redirect on 401 errors
    // Let the components and AuthContext handle authentication state
    // This prevents redirect loops and allows proper error handling
    
    return Promise.reject(error);
  }
);

// Auth API methods
export const signup = async (userData) => {
  return await api.post('/auth/signup', userData);
};

export const login = async (credentials) => {
  return await api.post('/auth/login', credentials);
};

export const verifyEmailOtp = async (payload) => {
  return await api.post('/auth/verify-email-otp', payload);
};

export const resendEmailOtp = async (payload) => {
  return await api.post('/auth/resend-email-otp', payload);
};

export const forgotPassword = async (payload) => {
  return await api.post('/auth/forgot-password', payload);
};

export const verifyPasswordResetOtp = async (payload) => {
  return await api.post('/auth/verify-password-reset-otp', payload);
};

export const resetPassword = async (payload) => {
  return await api.post('/auth/reset-password', payload);
};

export const joinWaitlist = async (waitlistData) => {
  return await api.post('/waitlist', waitlistData);
};

// Company Auth API methods
export const companySignup = async (companyData) => {
  return await api.post('/company/auth/signup', companyData);
};

export const companyLogin = async (credentials) => {
  return await api.post('/company/auth/login', credentials);
};

export const getCurrentCompany = async () => {
  return await api.get('/company/auth/me');
};

// Company Profile API methods
export const getCompanyProfile = async () => {
  return await api.get('/company/profile');
};

export const updateCompanyProfile = async (profileData) => {
  return await api.put('/company/profile', profileData);
};

export const getCompanyById = async (companyId) => {
  return await api.get(`/companies/${companyId}`);
};

export const followCompany = async (companyId) => {
  return await api.post(`/companies/${companyId}/follow`);
};

export const unfollowCompany = async (companyId) => {
  return await api.delete(`/companies/${companyId}/follow`);
};

// User follow/unfollow
export const followUser = async (userId) => {
  const response = await api.post(`/users/${userId}/follow`);
  return response.data;
};

export const unfollowUser = async (userId) => {
  const response = await api.delete(`/users/${userId}/follow`);
  return response.data;
};

export const getCompanies = async (params = {}) => {
  return await api.get('/users/companies', { params });
};

// Company Analytics API methods
export const getCompanyAnalytics = async (params = {}) => {
  return await api.get('/company/analytics', { params });
};

// Company Applications API methods
export const getCompanyApplications = async (params = {}) => {
  return await api.get('/company/applications', { params });
};

export const updateApplicationStatus = async (applicationId, data) => {
  return await api.put(`/company/applications/${applicationId}/status`, data);
};

export const exportApplications = async (params = {}) => {
  const response = await api.get('/company/applications/export', { 
    params,
    responseType: 'blob'
  });
  return response;
};

// Company Events API methods
export const getCompanyEvents = async (params = {}) => {
  return await api.get('/company/events', { params });
};

export const createCompanyEvent = async (eventData) => {
  return await api.post('/company/events', eventData);
};

export const updateCompanyEvent = async (eventId, eventData) => {
  return await api.put(`/company/events/${eventId}`, eventData);
};

export const deleteCompanyEvent = async (eventId) => {
  return await api.delete(`/company/events/${eventId}`);
};

export const getCompanyEventRegistrations = async (eventId) => {
  return await api.get(`/company/events/${eventId}/registrations`);
};

// Company Jobs API methods
export const createJob = async (jobData) => {
  return await api.post('/company/jobs', jobData);
};

export const getCompanyJobs = async (params = {}) => {
  return await api.get('/company/jobs', { params });
};

export const updateJob = async (jobId, jobData) => {
  return await api.put(`/company/jobs/${jobId}`, jobData);
};

export const deleteJob = async (jobId) => {
  return await api.delete(`/company/jobs/${jobId}`);
};

// Student Jobs API methods
export const getJobs = async (params = {}) => {
  return await api.get('/jobs', { params });
};

export const getJobById = async (jobId) => {
  return await api.get(`/jobs/${jobId}`);
};

// Company Internships API methods
export const createInternship = async (internshipData) => {
  return await api.post('/company/internships', internshipData);
};

export const getCompanyInternships = async (params = {}) => {
  return await api.get('/company/internships', { params });
};

export const updateInternship = async (internshipId, internshipData) => {
  return await api.put(`/company/internships/${internshipId}`, internshipData);
};

export const deleteInternship = async (internshipId) => {
  return await api.delete(`/company/internships/${internshipId}`);
};

// Student Internships API methods
export const getInternships = async (params = {}) => {
  return await api.get('/internships', { params });
};

export const getInternshipById = async (internshipId) => {
  return await api.get(`/internships/${internshipId}`);
};

// Student Applications API methods
export const getMyApplications = async () => {
  return await api.get('/applications');
};

// Company College Selection API methods
export const getAvailableColleges = async () => {
  return await api.get('/company/colleges/available');
};

export const joinColleges = async (data) => {
  return await api.post('/company/colleges/join', data);
};

export const leaveCollege = async (collegeId) => {
  return await api.delete(`/company/colleges/${collegeId}`);
};

// Company Communities API methods
export const createCompanyCommunity = async (communityData) => {
  return await api.post('/company/communities', communityData);
};

export const getCompanyCommunities = async (params = {}) => {
  return await api.get('/company/communities', { params });
};

export const updateCompanyCommunity = async (communityId, communityData) => {
  return await api.put(`/company/communities/${communityId}`, communityData);
};

export const deleteCompanyCommunity = async (communityId) => {
  return await api.delete(`/company/communities/${communityId}`);
};

// User API methods
export const getCurrentUser = async () => {
  return await api.get('/users/me');
};

export const getUserById = async (id) => {
  return await api.get(`/users/${id}`);
};

export const updateProfile = async (profileData) => {
  return await api.put('/users/me', profileData);
};

export const getUserStats = async () => {
  return await api.get('/users/stats');
};

export const getUserPosts = async () => {
  return await api.get('/users/me/posts');
};

export const getUserPostsById = async (userId) => {
  return await api.get(`/users/${userId}/posts`);
};

// Profile picture API methods
export const uploadProfilePicture = async (formData) => {
  return await api.post('/users/profile-picture', formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  });
};

export const deleteProfilePicture = async () => {
  return await api.delete('/users/profile-picture');
};

// Cover photo API methods
export const uploadCoverPhoto = async (formData) => {
  return await api.post('/users/cover-photo', formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  });
};

export const deleteCoverPhoto = async () => {
  return await api.delete('/users/cover-photo');
};

// Student settings API methods
export const getStudentSettings = async () => {
  return await api.get('/users/settings');
};

export const updateStudentAccountSettings = async (data) => {
  return await api.patch('/users/settings/account', data);
};

export const updateStudentProfileSettings = async (data) => {
  return await api.patch('/users/settings/profile', data);
};

export const changeStudentPassword = async (data) => {
  return await api.patch('/users/settings/password', data);
};

export const updateStudentNotificationSettings = async (data) => {
  return await api.patch('/users/settings/notifications', data);
};

export const updateStudentPreferenceSettings = async (data) => {
  return await api.patch('/users/settings/preferences', data);
};

export const updateStudentPrivacySettings = async (data) => {
  return await api.patch('/users/settings/privacy', data);
};

export const getStudentBlockedUsers = async () => {
  return await api.get('/users/settings/blocked-users');
};

export const addStudentBlockedUser = async (userId) => {
  return await api.post('/users/settings/blocked-users', { userId });
};

export const removeStudentBlockedUser = async (userId) => {
  return await api.delete(`/users/settings/blocked-users/${userId}`);
};

export const getStudentMutedUsers = async () => {
  return await api.get('/users/settings/muted-users');
};

export const addStudentMutedUser = async (userId) => {
  return await api.post('/users/settings/muted-users', { userId });
};

export const removeStudentMutedUser = async (userId) => {
  return await api.delete(`/users/settings/muted-users/${userId}`);
};

export const exportStudentSettingsData = async () => {
  return await api.get('/users/settings/export');
};

export const deactivateStudentAccount = async (data) => {
  return await api.post('/users/settings/deactivate', data);
};

// ============================================================================
// UNIFIED FEED API METHODS (New - Recommended)
// ============================================================================

/**
 * Get feed for a specific college
 * @param {string} collegeId - The college identifier
 * @param {object} params - Query parameters (category, limit, offset)
 * @returns {Promise} Feed data with posts
 */
export const getFeed = async (collegeId, params = {}) => {
  return await api.get(`/college/${collegeId}/feed`, { params });
};

/**
 * Create a post in a specific college feed
 * @param {string} collegeId - The college identifier
 * @param {object} postData - Post content and metadata
 * @returns {Promise} Created post data
 */
export const createPostUnified = async (collegeId, postData) => {
  // Check if postData contains images (File objects)
  if (postData.images && postData.images.length > 0) {
    // Use FormData for multipart/form-data
    const formData = new FormData();
    formData.append('content', postData.content);
    formData.append('category', postData.category);
    
    // Append each image file
    postData.images.forEach((image) => {
      formData.append('images', image);
    });
    
    return await api.post(`/college/${collegeId}/feed`, formData);
  } else {
    // Regular JSON request (no images)
    return await api.post(`/college/${collegeId}/feed`, postData);
  }
};

/**
 * Like or unlike a post in a specific college feed
 * @param {string} collegeId - The college identifier
 * @param {string} postId - The post identifier
 * @returns {Promise} Updated like data
 */
export const likePostUnified = async (collegeId, postId) => {
  return await api.post(`/college/${collegeId}/feed/${postId}/like`);
};

/**
 * Add a comment to a post in a specific college feed
 * @param {string} collegeId - The college identifier
 * @param {string} postId - The post identifier
 * @param {string} content - Comment content
 * @returns {Promise} Created comment data
 */
export const addCommentUnified = async (collegeId, postId, content) => {
  return await api.post(`/college/${collegeId}/feed/${postId}/comments`, { content });
};

// ============================================================================
// LEGACY POST API METHODS (Deprecated - Use unified methods above)
// ============================================================================

/**
 * @deprecated Use createPostUnified(collegeId, postData) instead
 * Create a post using legacy route
 */
export const createPost = async (postData) => {
  // Check if postData contains images (File objects)
  if (postData.images && postData.images.length > 0) {
    // Use FormData for multipart/form-data
    const formData = new FormData();
    formData.append('content', postData.content);
    formData.append('category', postData.category);

    // Append each image file (cover should be index 0 if relevant).
    postData.images.forEach((image) => {
      formData.append('images', image);
    });

    // Forward any extra primitive/JSON fields supplied by the caller, e.g.
    // `projectContent` from the new CreateProjectModal — objects get
    // serialized to JSON so the backend can `JSON.parse` them safely.
    const reserved = new Set(['content', 'category', 'images']);
    Object.entries(postData).forEach(([key, value]) => {
      if (reserved.has(key) || value === undefined || value === null) return;
      if (typeof value === 'object' && !(value instanceof File) && !(value instanceof Blob)) {
        formData.append(key, JSON.stringify(value));
      } else if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
        formData.append(key, String(value));
      }
    });

    // Axios sets Content-Type + boundary automatically for FormData
    return await api.post('/posts', formData);
  } else {
    // Regular JSON request (no images) — projectContent passes through
    // as a regular JSON field on the body.
    return await api.post('/posts', postData);
  }
};

/**
 * @deprecated Use getFeed(collegeId, params) instead
 * Get posts using legacy route
 */
export const getPosts = async (params = {}) => {
  return await api.get('/posts', { params });
};

export const getPostById = async (id) => {
  return await api.get(`/posts/${id}`);
};

export const deletePost = async (id) => {
  return await api.delete(`/posts/${id}`);
};

/**
 * @deprecated Use likePostUnified(collegeId, postId) instead
 * Like/Unlike post using legacy route
 */
export const likePost = async (id) => {
  return await api.post(`/posts/${id}/like`);
};

export const markPostInterested = async (id) => {
  return await api.post(`/posts/${id}/interested`);
};

export const joinPostEvent = async (id) => {
  return await api.post(`/posts/${id}/join`);
};

export const bookmarkPost = async (id) => {
  return await api.post(`/posts/${id}/bookmark`);
};

// Rate a project post
export const ratePost = async (id, rating) => {
  return await api.post(`/posts/${id}/rate`, { rating });
};

/**
 * @deprecated Use addCommentUnified(collegeId, postId, content) instead
 * Comment on post using legacy route
 */
export const addComment = async (id, content, options = {}) => {
  const body = { content };
  if (options.isAnonymous === true) body.isAnonymous = true;
  return await api.post(`/posts/${id}/comments`, body);
};

export const getComments = async (id) => {
  return await api.get(`/posts/${id}/comments`);
};

export const replyToComment = async (postId, commentId, content, options = {}) => {
  const body = { content };
  if (options.isAnonymous === true) body.isAnonymous = true;
  return await api.post(`/posts/${postId}/comments/${commentId}/replies`, body);
};

export const getCommentReplies = async (postId, commentId) => {
  return await api.get(`/posts/${postId}/comments/${commentId}/replies`);
};

export const likeComment = async (postId, commentId) => {
  return await api.post(`/posts/${postId}/comments/${commentId}/like`);
};

export const deleteComment = async (postId, commentId) => {
  return await api.delete(`/posts/${postId}/comments/${commentId}`);
};

// ============================================================================
// UNIFIED LEADERBOARD API METHODS (New - Recommended)
// ============================================================================

/**
 * Get leaderboard for a specific college
 * @param {string} collegeId - The college identifier
 * @param {string} category - Leaderboard category (overall, weekly, monthly)
 * @returns {Promise} Leaderboard data
 */
export const getLeaderboardUnified = async (collegeId, category = 'overall') => {
  return await api.get(`/college/${collegeId}/leaderboard`, { params: { category } });
};

// ============================================================================
// UNIFIED HASHTAG API METHODS (New - Recommended)
// ============================================================================

/**
 * Get trending hashtags for a specific college
 * @param {string} collegeId - The college identifier
 * @returns {Promise} Trending hashtags data
 */
export const getTrendingHashtagsUnified = async (collegeId) => {
  return await api.get(`/college/${collegeId}/trending/hashtags`);
};

// ============================================================
// SNAPS API METHODS — 24-hour Instagram-style stories
// ============================================================

/**
 * Get all active snaps for a college
 * @param {string} collegeId - The college identifier
 */
export const getSnaps = async (collegeId) => {
  return await api.get(`/college/${collegeId}/snaps`);
};

/**
 * Create a new snap (image required, caption optional max 50 chars, category optional)
 * @param {string} collegeId - The college identifier
 * @param {File} imageFile - The image file
 * @param {string} caption - Optional caption (max 50 chars)
 * @param {string} category - Optional category: general, project, event, gossip
 */
export const createSnap = async (collegeId, imageFile, caption = '', category = 'general') => {
  const formData = new FormData();
  formData.append('image', imageFile);
  if (caption) formData.append('caption', caption);
  if (category) formData.append('category', category);
  return await api.post(`/college/${collegeId}/snaps`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
};

/**
 * Mark a snap as viewed
 * @param {string} collegeId - The college identifier
 * @param {string} snapId - The snap identifier
 */
export const viewSnap = async (collegeId, snapId) => {
  return await api.post(`/college/${collegeId}/snaps/${snapId}/view`);
};

// ============================================================================
// LEGACY HASHTAG API METHODS (Deprecated - Use unified methods above)
// ============================================================================

/**
 * @deprecated Use getTrendingHashtagsUnified(collegeId) instead
 */
export const getTrendingHashtags = async () => {
  return await api.get('/posts/trending/hashtags');
};

// Get posts by hashtag
export const getPostsByHashtag = async (hashtag) => {
  return await api.get(`/posts/hashtag/${hashtag}`);
};

// ============================================================================
// UNIFIED EVENT API METHODS (New - Recommended)
// ============================================================================

/**
 * Get events for a specific college
 * @param {string} collegeId - The college identifier
 * @param {object} params - Query parameters (status, limit, offset)
 * @returns {Promise} Events data
 */
export const getEventsUnified = async (collegeId, params = {}) => {
  return await api.get(`/college/${collegeId}/events`, { params });
};

// ============================================================================
// LEGACY EVENT API METHODS (Deprecated - Use unified methods above)
// ============================================================================

/**
 * @deprecated Use getEventsUnified(collegeId, params) instead
 */
export const createEvent = async (eventData) => {
  return await api.post('/events', eventData);
};

/**
 * @deprecated Use getEventsUnified(collegeId, params) instead
 */
export const getEvents = async (params = {}) => {
  return await api.get('/events', { params });
};

export const getEventById = async (id) => {
  return await api.get(`/events/${id}`);
};

export const deleteEvent = async (id) => {
  return await api.delete(`/events/${id}`);
};

// Mark interested/uninterested in event
export const markInterested = async (id) => {
  return await api.post(`/events/${id}/interested`);
};

// Join/leave event
export const joinEvent = async (id) => {
  return await api.post(`/events/${id}/join`);
};

// Recommend/unrecommend event (company only)
export const recommendEvent = async (id, params = {}) => {
  return await api.post(`/events/${id}/recommend`, {}, { params });
};

// Event registration API methods
export const registerForEvent = async (eventId, formData) => {
  return await api.post(`/events/${eventId}/register`, formData);
};

export const getEventRegistrations = async (eventId) => {
  return await api.get(`/events/${eventId}/registrations`);
};

export const cancelRegistration = async (eventId) => {
  return await api.delete(`/events/${eventId}/register`);
};

// ============================================================================
// UNIFIED CONNECTION API METHODS (New - Recommended)
// ============================================================================

/**
 * Get connections for a specific college
 * @param {string} collegeId - The college identifier
 * @returns {Promise} Connections data
 */
export const getConnectionsUnified = async (collegeId) => {
  return await api.get(`/college/${collegeId}/connections`);
};

/**
 * Get suggested connections for a specific college
 * @param {string} collegeId - The college identifier (used for company preview mode)
 * @returns {Promise} Suggested connections data
 */
export const getSuggestedConnectionsUnified = async (collegeId) => {
  const params = collegeId ? { college: collegeId } : undefined;
  return await api.get('/connections/suggestions', { params });
};

// ============================================================================
// LEGACY CONNECTION API METHODS (Deprecated - Use unified methods above)
// ============================================================================

/**
 * @deprecated Use getConnectionsUnified(collegeId) instead
 */
export const sendConnectionRequest = async (userId) => {
  return await api.post(`/connections/request/${userId}`);
};

export const acceptConnectionRequest = async (userId) => {
  return await api.post(`/connections/accept/${userId}`);
};

export const rejectConnectionRequest = async (userId) => {
  return await api.post(`/connections/reject/${userId}`);
};

/**
 * @deprecated Use getConnectionsUnified(collegeId) instead
 */
export const getConnections = async () => {
  return await api.get('/connections');
};

export const getPendingRequests = async () => {
  return await api.get('/connections/requests');
};

/**
 * @deprecated Use getSuggestedConnectionsUnified(collegeId) instead
 */
export const getSuggestedConnections = async (collegeId) => {
  return getSuggestedConnectionsUnified(collegeId);
};

// Prospect API methods (Company only)
export const markAsProspect = async (data) => {
  return await api.post('/prospects', data);
};

export const getProspects = async (params) => {
  return await api.get('/prospects', { params });
};

export const getProspectById = async (prospectId) => {
  return await api.get(`/prospects/${prospectId}`);
};

export const updateProspect = async (prospectId, data) => {
  return await api.put(`/prospects/${prospectId}`, data);
};

export const removeProspect = async (prospectId) => {
  return await api.delete(`/prospects/${prospectId}`);
};

export const checkProspectStatus = async (studentId) => {
  return await api.get(`/prospects/check/${studentId}`);
};

// Message API methods
export const getConversations = async () => {
  return await api.get('/messages/conversations');
};

export const getChatHistory = async (userId) => {
  return await api.get(`/messages/${userId}`);
};

export const sendMessage = async (userId, content, replyTo = null) => {
  const body = { content };
  if (replyTo) body.replyTo = replyTo;
  return await api.post(`/messages/${userId}`, body);
};

export const markMessagesAsRead = async (userId) => {
  return await api.put(`/messages/${userId}/read`);
};

export const getUnreadCount = async () => {
  return await api.get('/messages/unread/count');
};

// Message Interaction API methods
export const editMessage = async (messageId, content) => {
  return await api.put(`/messages/${messageId}/edit`, { content });
};

export const deleteMessage = async (messageId) => {
  return await api.delete(`/messages/${messageId}`);
};

export const addReaction = async (messageId, emoji) => {
  return await api.post(`/messages/${messageId}/reactions`, { emoji });
};

export const removeReaction = async (messageId, emoji) => {
  return await api.delete(`/messages/${messageId}/reactions/${emoji}`);
};

export const pinMessage = async (messageId) => {
  return await api.post(`/messages/${messageId}/pin`);
};

export const unpinMessage = async (messageId) => {
  return await api.delete(`/messages/${messageId}/pin`);
};

export const getPinnedMessages = async (userId) => {
  return await api.get(`/messages/${userId}/pinned`);
};

export const uploadFileMessage = async (userId, formData) => {
  return await api.post(`/messages/${userId}/upload`, formData, {
    headers: {
      'Content-Type': undefined
    }
  });
};

// ============================================================================
// UNIFIED GROUP/COMMUNITY API METHODS (New - Recommended)
// ============================================================================

/**
 * Get communities/groups for a specific college
 * @param {string} collegeId - The college identifier
 * @param {object} filters - Query parameters (search, filter, category)
 * @returns {Promise} Communities data
 */
export const getCommunities = async (collegeId, filters = {}) => {
  try {
    const params = {
      college: collegeId,
      ...filters
    };
    
    // Remove undefined/null values
    Object.keys(params).forEach(key => {
      if (params[key] === undefined || params[key] === null || params[key] === '') {
        delete params[key];
      }
    });
    
    return await api.get('/groups', { params });
  } catch (error) {
    const message = error.response?.data?.message || 'Failed to load communities. Please try again.';
    throw new Error(message);
  }
};

/**
 * Create a new community
 * @param {object} data - Community data (name, description, category, privacy, approvalType, rules, coverImage, avatar)
 * @returns {Promise} Created community data
 */
export const createCommunity = async (data) => {
  try {
    // Check if data contains images (File objects)
    if ((data.coverImage && data.coverImage instanceof File) || (data.avatar && data.avatar instanceof File)) {
      // Use FormData for multipart/form-data
      const formData = new FormData();
      
      // Append text fields
      formData.append('name', data.name);
      formData.append('description', data.description);
      formData.append('category', data.category);
      formData.append('privacy', data.privacy);
      formData.append('approvalType', data.approvalType);
      
      if (data.rules) {
        formData.append('rules', data.rules);
      }
      
      // Append image files
      if (data.coverImage instanceof File) {
        formData.append('coverImage', data.coverImage);
      }
      
      if (data.avatar instanceof File) {
        formData.append('avatar', data.avatar);
      }
      
      // Send with multipart/form-data content type
      return await api.post('/groups', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
    } else {
      // Regular JSON request (no images or images are URLs)
      return await api.post('/groups', data);
    }
  } catch (error) {
    const message = error.response?.data?.message || 'Failed to create community. Please try again.';
    throw new Error(message);
  }
};

/**
 * Join a community (send join request for private communities)
 * @param {string} groupId - The community/group identifier
 * @returns {Promise} Join response data
 */
export const joinCommunity = async (groupId) => {
  try {
    return await api.post(`/groups/${groupId}/request`);
  } catch (error) {
    const message = error.response?.data?.message || 'Failed to join community. Please try again.';
    throw new Error(message);
  }
};

/**
 * Leave a community
 * @param {string} groupId - The community/group identifier
 * @returns {Promise} Leave response data
 */
export const leaveCommunity = async (groupId) => {
  try {
    return await api.post(`/groups/${groupId}/leave`);
  } catch (error) {
    const message = error.response?.data?.message || 'Failed to leave community. Please try again.';
    throw new Error(message);
  }
};

/**
 * Get suggested communities for a specific college
 * @param {string} collegeId - The college identifier
 * @returns {Promise} Suggested communities data
 */
export const getSuggestedCommunities = async (collegeId) => {
  try {
    const params = {
      college: collegeId
    };
    
    return await api.get('/groups/suggestions', { params });
  } catch (error) {
    const message = error.response?.data?.message || 'Failed to load suggested communities. Please try again.';
    throw new Error(message);
  }
};

/**
 * @deprecated Use getCommunities(collegeId, filters) instead
 * Get communities/groups for a specific college (old unified route)
 */
export const getCommunitiesUnified = async (collegeId, params = {}) => {
  return await api.get(`/college/${collegeId}/communities`, { params });
};

/**
 * @deprecated Use getSuggestedCommunities(collegeId) instead
 * Get suggested communities for a specific college (old unified route)
 */
export const getSuggestedCommunitiesUnified = async (collegeId) => {
  return await api.get(`/college/${collegeId}/communities/suggestions`);
};

// ============================================================================
// LEGACY GROUP API METHODS (Deprecated - Use unified methods above)
// ============================================================================

/**
 * @deprecated Use getCommunitiesUnified(collegeId, params) instead
 */
export const createGroup = async (groupData) => {
  return await api.post('/groups', groupData);
};

/**
 * @deprecated Use getCommunitiesUnified(collegeId, params) instead
 */
export const getGroups = async (urlOrParams) => {
  // Support both direct URL string and query parameters
  if (typeof urlOrParams === 'string') {
    return await api.get(urlOrParams);
  }
  return await api.get('/groups');
};

export const getGroupById = async (groupId) => {
  return await api.get(`/groups/${groupId}`);
};

export const updateGroup = async (groupId, updates) => {
  return await api.put(`/groups/${groupId}`, updates);
};

export const deleteGroup = async (groupId) => {
  return await api.delete(`/groups/${groupId}`);
};

export const requestToJoin = async (groupId) => {
  return await api.post(`/groups/${groupId}/request`);
};

export const approveJoinRequest = async (groupId, userId) => {
  return await api.post(`/groups/${groupId}/approve`, { userId });
};

export const rejectJoinRequest = async (groupId, userId) => {
  return await api.post(`/groups/${groupId}/reject`, { userId });
};

export const joinViaInviteLink = async (inviteLink) => {
  return await api.post(`/groups/join/${inviteLink}`);
};

export const leaveGroup = async (groupId) => {
  return await api.post(`/groups/${groupId}/leave`);
};

export const updateMessagePermission = async (groupId, permission) => {
  return await api.put(`/groups/${groupId}/permissions`, { messagePermission: permission });
};

export const updateAllowedSenders = async (groupId, senders) => {
  return await api.put(`/groups/${groupId}/allowed-senders`, { allowedSenders: senders });
};

export const updateGroupPermissions = async (groupId, permissions) => {
  // Handle message permissions
  if (permissions.messagePermission !== undefined) {
    await api.put(`/groups/${groupId}/permissions`, {
      messagePermission: permissions.messagePermission
    });
  }
  
  // Handle post permissions
  if (permissions.postPermission !== undefined) {
    await api.put(`/groups/${groupId}/post-permissions`, {
      postPermission: permissions.postPermission
    });
  }
  
  return { success: true };
};

export const removeMemberFromGroup = async (groupId, userId) => {
  return await api.post(`/groups/${groupId}/remove-member`, { userId });
};

export const getGroupMessages = async (groupId) => {
  return await api.get(`/groups/${groupId}/messages`);
};

export const sendGroupMessage = async (groupId, content) => {
  return await api.post(`/groups/${groupId}/messages`, { content });
};

// Profile API methods
export const getUserProfile = async (userId) => {
  return await api.get(`/users/${userId}/profile`);
};

export const updateUserProfile = async (data) => {
  return await api.patch('/users/profile', data);
};

export const pinPostToProfile = async (postId) => {
  return await api.put('/users/me/pinned-post', { postId: postId || null });
};

export const endorseSkill = async (userId, skillName) => {
  return await api.post(`/users/${userId}/skills/${encodeURIComponent(skillName)}/endorse`);
};

export const getProfileViewers = async () => {
  return await api.get('/users/me/profile-viewers');
};

// Search API methods
export const searchAll = async (query, collegeId) => {
  return await api.get('/search', { params: { q: query, college: collegeId } });
};

// ============================================================================
// COMMUNITY DETAIL API METHODS (Community Section Redesign)
// ============================================================================

export const getCommunityStats = async (groupId) => {
  return await api.get(`/groups/${groupId}/stats`);
};

export const getCommunityFeed = async (groupId, params = {}) => {
  return await api.get(`/groups/${groupId}/feed`, { params });
};

export const getCommunityDiscussions = async (groupId, params = {}) => {
  return await api.get(`/groups/${groupId}/discussions`, { params });
};

export const getCommunityResources = async (groupId, params = {}) => {
  return await api.get(`/groups/${groupId}/resources`, { params });
};

export const getCommunityEvents = async (groupId) => {
  return await api.get(`/groups/${groupId}/events`);
};

export const getJoinedCommunityResources = async (params = {}) => {
  return await api.get('/groups/joined/resources', { params });
};

const postMultipart = async (urlPath, formData) => {
  const token = localStorage.getItem('token');
  const baseURL = getApiBaseUrl();
  const response = await fetch(`${baseURL}${urlPath}`, {
    method: 'POST',
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {})
    },
    body: formData
  });
  const json = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(json.message || 'Request failed');
    error.response = { data: json, status: response.status };
    throw error;
  }
  return json;
};

export const uploadCommunityResourceFile = async (file) => {
  const formData = new FormData();
  formData.append('file', file);
  return postMultipart('/upload/community-resource', formData);
};

export const downloadCommunityResource = async (postId) => {
  return await api.get(`/groups/resources/${postId}/download`);
};

export const pinCommunityPost = async (groupId, postId) => {
  return await api.post(`/groups/${groupId}/posts/${postId}/pin`);
};

export const unpinCommunityPost = async (groupId, postId) => {
  return await api.delete(`/groups/${groupId}/posts/${postId}/pin`);
};

export const createCommunityPost = async (groupId, data, file = null) => {
  if (file) {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('content', data.content);
    formData.append('postType', data.postType || 'discussion');
    if (data.pollData) formData.append('pollData', JSON.stringify(data.pollData));
    if (data.resourceMeta) formData.append('resourceMeta', JSON.stringify(data.resourceMeta));
    return postMultipart(`/groups/${groupId}/feed`, formData);
  }
  return await api.post(`/groups/${groupId}/feed`, data);
};

export const deleteCommunityPost = async (groupId, postId) => {
  return await api.delete(`/groups/${groupId}/posts/${postId}`);
};

export const voteCommunityPoll = async (groupId, postId, optionIndex) => {
  return await api.post(`/groups/${groupId}/posts/${postId}/vote`, { optionIndex });
};

// ============================================================================
// PLACEMENT ZONE API METHODS
// ============================================================================

// --- Placement Discover APIs ---

export const getDiscoverData = async () => {
  return await api.get('/placement/discover');
};

export const getReadinessScore = async () => {
  return await api.get('/placement/discover/readiness');
};

export const getRecommendations = async () => {
  return await api.get('/placement/discover/recommendations');
};

export const getSkillDemand = async () => {
  return await api.get('/placement/discover/skill-demand');
};

export const getTrendingCompanies = async () => {
  return await api.get('/placement/discover/trending-companies');
};

// --- Placement Company APIs ---

export const getPlacementCompanies = async (params = {}) => {
  return await api.get('/placement/companies', { params });
};

export const getPlacementCompanyProfile = async (companyId) => {
  return await api.get(`/placement/companies/${companyId}`);
};

export const getHiringTrends = async (companyId) => {
  return await api.get(`/placement/companies/${companyId}/hiring-trends`);
};

export const getInterviewExperiences = async (companyId, params = {}) => {
  return await api.get(`/placement/companies/${companyId}/interviews`, { params });
};

export const submitInterviewExperience = async (companyId, data) => {
  return await api.post(`/placement/companies/${companyId}/interviews`, data);
};

export const followPlacementCompany = async (companyId) => {
  return await api.post(`/companies/${companyId}/follow`);
};

export const unfollowPlacementCompany = async (companyId) => {
  return await api.delete(`/companies/${companyId}/follow`);
};

// --- Placement Room APIs ---

export const getRooms = async (params = {}) => {
  return await api.get('/placement/rooms', { params });
};

export const getRoomDetail = async (roomId) => {
  return await api.get(`/placement/rooms/${roomId}`);
};

export const joinRoom = async (roomId) => {
  return await api.post(`/placement/rooms/${roomId}/join`);
};

export const leaveRoom = async (roomId) => {
  return await api.post(`/placement/rooms/${roomId}/leave`);
};

export const getRoomDiscussions = async (roomId, params = {}) => {
  return await api.get(`/placement/rooms/${roomId}/discussions`, { params });
};

export const getRoomDiscussion = async (roomId, postId) => {
  return await api.get(`/placement/rooms/${roomId}/discussions/${postId}`);
};

export const createRoomDiscussion = async (roomId, data) => {
  return await api.post(`/placement/rooms/${roomId}/discussions`, data);
};

export const replyToRoomDiscussion = async (roomId, postId, data) => {
  return await api.post(`/placement/rooms/${roomId}/discussions/${postId}/replies`, data);
};

export const voteOnRoomPost = async (roomId, postId, data) => {
  return await api.post(`/placement/rooms/${roomId}/discussions/${postId}/vote`, data);
};

export const voteOnRoomReply = async (roomId, postId, replyId, data) => {
  return await api.post(`/placement/rooms/${roomId}/discussions/${postId}/replies/${replyId}/vote`, data);
};

export const markDiscussionBestAnswer = async (roomId, postId, data) => {
  return await api.post(`/placement/rooms/${roomId}/discussions/${postId}/best-answer`, data);
};

export const toggleDiscussionBookmark = async (roomId, postId) => {
  return await api.post(`/placement/rooms/${roomId}/discussions/${postId}/bookmark`);
};

export const getRoomAnnouncements = async (roomId) => {
  return await api.get(`/placement/rooms/${roomId}/announcements`);
};

export const getRoomResources = async (roomId) => {
  return await api.get(`/placement/rooms/${roomId}/resources`);
};

export const getRoomTrending = async (roomId) => {
  return await api.get(`/placement/rooms/${roomId}/trending`);
};

export const createCompanyPlacementRoom = async (data) => {
  return await api.post('/company/placement-rooms', data);
};

export const getCompanyPlacementRooms = async () => {
  return await api.get('/company/placement-rooms');
};

export const postCompanyRoomAnnouncement = async (roomId, data) => {
  return await api.post(`/company/placement-rooms/${roomId}/announcements`, data);
};

export const uploadCompanyRoomResource = async (roomId, data) => {
  return await api.post(`/company/placement-rooms/${roomId}/resources`, data);
};

// --- Challenge APIs ---

export const getChallenges = async (params = {}) => {
  return await api.get('/placement/challenges', { params });
};

export const getChallengeDetail = async (challengeId) => {
  return await api.get(`/placement/challenges/${challengeId}`);
};

export const registerForChallenge = async (challengeId) => {
  return await api.post(`/placement/challenges/${challengeId}/register`);
};

export const submitChallengeSolution = async (challengeId, data) => {
  return await api.post(`/placement/challenges/${challengeId}/submit`, data);
};

export const getChallengeLeaderboard = async (challengeId) => {
  return await api.get(`/placement/challenges/${challengeId}/leaderboard`);
};

// --- Leaderboard APIs ---

export const getPlacementLeaderboard = async (params = {}) => {
  return await api.get('/placement/leaderboard', { params });
};

export const getMyRank = async () => {
  return await api.get('/placement/leaderboard/my-rank');
};

export const getTopThree = async () => {
  return await api.get('/placement/leaderboard/top');
};

// --- Application APIs (Extended) ---

export const getApplicationsWithPipeline = async (params = {}) => {
  return await api.get('/applications/pipeline', { params });
};

export const getApplicationDetail = async (applicationId) => {
  return await api.get(`/applications/${applicationId}`);
};

export const submitApplication = async (data) => {
  return await api.post('/applications', data);
};

export const withdrawApplication = async (applicationId) => {
  return await api.put(`/applications/${applicationId}/withdraw`);
};

export const getUpcomingInterviews = async () => {
  return await api.get('/applications/upcoming');
};

// --- Saved Items APIs ---

export const getSavedItems = async (params = {}) => {
  return await api.get('/placement/saved', { params });
};

export const saveItemApi = async (data) => {
  return await api.post('/placement/saved', data);
};

export const removeSavedItemApi = async (savedItemId) => {
  return await api.delete(`/placement/saved/${savedItemId}`);
};

// --- Search APIs ---

export const globalSearch = async (query, params = {}) => {
  return await api.get('/placement/search', { params: { q: query, ...params } });
};

export const getSearchSuggestions = async (query) => {
  return await api.get('/placement/search/suggestions', { params: { q: query } });
};

export default api;
